import { useState, useRef, useEffect } from 'react';
import { Title1, Text, Spinner, makeStyles, tokens, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { SearchInfo24Regular } from '@fluentui/react-icons';
import { TrackingFilters, type TrackingSearchParams } from '../../components/Logs/TrackingFilters';
import { OperationsTable, type OperationSummary } from '../../components/Logs/OperationsTable';
import { logsService } from '../../services/logsService';
import type { LogEntry, OperationGroup } from '../../types';
import { messageTypeMapping, normalizeOperation } from '../../utils/messageTypeMapping';
import { operationInvolvesClass } from '../../utils/operationFormatter';
import { useExport } from '../../contexts/ExportContext';

/**
 * Verifica se há filtros ativos nos parâmetros de busca
 */
function hasActiveFilters(params: TrackingSearchParams | null): boolean {
  if (!params) return false;
  return !!(
    params.dateFrom || params.dateTo || params.searchTerm ||
    params.externalId || params.correlationId || params.operation ||
    params.userType || params.status || params.sectionId ||
    params.className || params.classCode
  );
}

/**
 * Exporta operações carregadas localmente para CSV (formato brasileiro)
 * Usado quando não há filtros ativos (carga inicial / limpar filtros)
 */
function exportLocalCsv(operations: OperationSummary[]) {
  if (operations.length === 0) return;

  const BOM = '\uFEFF';
  const sep = ';';
  const headers = [
    'Status', 'Data/Hora Inicio', 'Data/Hora Fim', 'Duracao',
    'Tipo', 'External ID', 'Nome', 'Email',
    'CorrelationId', 'Total Eventos', 'Erros', 'Avisos',
    'Tem Retry', 'Tem DLQ', 'Section ID'
  ];

  const formatDate = (d: Date | undefined) =>
    d ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

  const formatStatus = (s: string) => {
    switch (s) {
      case 'success': return 'Sucesso';
      case 'error': return 'Erro';
      case 'warning': return 'Aviso';
      case 'processing': return 'Processando';
      default: return s;
    }
  };

  const escape = (val: string) => `"${(val ?? '').replace(/"/g, '""')}"`;

  const rows = operations.map(op => [
    escape(formatStatus(op.status)),
    escape(formatDate(op.startTime)),
    escape(formatDate(op.endTime)),
    escape(op.duration ?? ''),
    escape(op.messageType),
    escape(op.externalId ?? ''),
    escape(op.displayName ?? ''),
    escape(op.userPrincipalName ?? ''),
    escape(op.correlationId),
    String(op.totalEvents),
    String(op.errorCount),
    String(op.warningCount),
    escape(op.hasRetries ? 'Sim' : 'Nao'),
    escape(op.hasDlq ? 'Sim' : 'Nao'),
    escape(op.sectionId ?? '')
  ].join(sep));

  const csv = BOM + headers.join(sep) + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  link.href = URL.createObjectURL(blob);
  link.download = `operacoes_${timestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    marginBottom: '12px',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    display: 'block',
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    marginTop: '24px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 48px',
    minHeight: '400px',
  },
  emptyStateIcon: {
    fontSize: '192px',
    color: 'rgba(30, 30, 30, 0.4)',
    marginBottom: '24px',
  },
  emptyStateText: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px',
    gap: '16px',
  },
  loadMoreButton: {
    minWidth: '200px',
  },
});

/**
 * Extrai o sectionId dos eventos (para Roster/Enrollment)
 * Busca no payloadJson dos eventos
 */
function extractSectionIdFromEvents(events: LogEntry[] | undefined): string | undefined {
  if (!events || events.length === 0) return undefined;

  for (const event of events) {
    if (event.payloadJson) {
      try {
        const payload = JSON.parse(event.payloadJson);
        // Tentar múltiplos caminhos possíveis (incluindo MAIÚSCULO do backend)
        const sectionId = payload.SECTIONEXTERNALID || payload.SectionExternalId ||
                         payload.sectionExternalId || payload.sectionId || payload.classId ||
                         payload.SectionId || payload.ClassId ||
                         payload.section?.id || payload.class?.id ||
                         payload.classExternalId;
        if (sectionId) return sectionId;
      } catch {
        // Ignorar erro de parsing
      }
    }
  }

  return undefined;
}

export const TrackingPage: React.FC = () => {
  const styles = useStyles();
  const [operations, setOperations] = useState<OperationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Estado para paginação por cursor
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Guardar parâmetros da última busca para "Carregar mais"
  const lastSearchParams = useRef<TrackingSearchParams | null>(null);

  // Export context (global, com toast de progresso)
  const { exportState, startExport } = useExport();

  /**
   * Carrega automaticamente TOP 50 operações mais recentes ao montar o componente
   * TOP 50 = 50 operações mais recentes baseadas no timestamp do API_RECEIVED
   * SEM filtro de data para ativar a estratégia de duas buscas no backend
   */
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      setHasSearched(true);

      try {
        // TOP 50: Sem datas para ativar estratégia de duas buscas no backend
        // Backend busca 50 API_RECEIVED mais recentes e todos os eventos de cada CorrelationId
        const result = await logsService.getGroupedOperations(
          undefined, // Sem data inicial
          undefined, // Sem data final
          undefined, // Sem messageType
          undefined, // Sem status
          50,        // TOP 50 operações
          undefined  // Sem cursor
        );
        const converted = result.operations.map(convertToOperationSummary);
        setOperations(converted);
        // TOP 50 não tem load more na carga inicial
        setNextCursor(null);
        setHasMore(false);
      } catch (err) {
        console.error('Erro ao carregar operações recentes:', err);
        setError('Erro ao carregar operações recentes.');
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    loadInitialData();
  }, []);

  /**
   * Converte OperationGroup (do backend) para OperationSummary (do frontend)
   */
  const convertToOperationSummary = (group: OperationGroup): OperationSummary => {
    const startTime = new Date(group.startTime);
    const endTime = group.endTime ? new Date(group.endTime) : startTime;

    // Usar sectionExternalId do backend (se disponivel) ou extrair dos eventos (fallback)
    const sectionId = operationInvolvesClass(group.messageType)
      ? (group.sectionExternalId || extractSectionIdFromEvents(group.events))
      : undefined;

    return {
      correlationId: group.correlationId,
      operation: group.events?.[0]?.operation || 'Unknown',
      messageType: group.messageType,
      startTime,
      endTime,
      duration: group.duration || '0ms',
      status: group.status,
      totalEvents: group.eventCount,
      errorCount: group.events?.filter(e => e.level === 'Error').length || 0,
      warningCount: group.events?.filter(e => e.level === 'Warning').length || 0,
      externalId: group.userExternalId,
      displayName: group.userDisplayName,
      userPrincipalName: group.userPrincipalName,
      hasDlqMessage: group.hasDlq,
      maxRetryCount: group.maxRetryCount,
      hasRetries: group.hasRetries,
      hasDlq: group.hasDlq,
      events: group.events || [],
      sectionId,
    };
  };

  /**
   * Prioridade de eventos para selecao de messageType.
   * Fluxo: API_RECEIVED -> QUEUE_PUBLISHED -> QUEUE_CONSUMED -> GRAPH_CALL_*
   * O primeiro evento (API_RECEIVED) tem os dados originais da operacao.
   */
  const getEventPriority = (operation?: string): number => {
    switch (operation?.toUpperCase()) {
      case 'API_RECEIVED': return 100;     // MAIOR prioridade - inicio do fluxo
      case 'QUEUE_PUBLISHED': return 90;   // API publica na fila
      case 'QUEUE_CONSUMED': return 80;    // Worker le da fila
      case 'GRAPH_CALL_SUCCESS':
      case 'GRAPH_CALL_FAILED':
      case 'GRAPH_CALL_SKIPPED': return 70;
      case 'RETRY_SCHEDULED': return 60;
      case 'SENT_TO_DLQ': return 50;
      default: return 0;
    }
  };

  /** Determina messageType do grupo com prioridade */
  const determineGroupMessageType = (events: LogEntry[]): string => {
    const validEvent = events
      .filter(e => e.messageType && e.messageType.trim() !== '' && e.messageType !== 'Entity')
      .sort((a, b) => getEventPriority(b.operation) - getEventPriority(a.operation))[0];
    return validEvent?.messageType || events[0]?.messageType || 'Unknown';
  };

  /** Determina operation do grupo com prioridade */
  const determineGroupOperation = (events: LogEntry[]): string => {
    const validEvent = events
      .filter(e => e.operation && e.operation.trim() !== '')
      .sort((a, b) => getEventPriority(b.operation) - getEventPriority(a.operation))[0];
    return validEvent?.operation || 'Unknown';
  };

  /**
   * Agrupa eventos de log por correlationId e cria OperationSummary
   * Usado apenas para busca por correlationId específico
   */
  const groupLogsByCorrelation = (logs: LogEntry[]): OperationSummary[] => {
    const grouped = new Map<string, Array<LogEntry & { originalIndex: number }>>();

    logs.forEach((log, index) => {
      const existing = grouped.get(log.correlationId) || [];
      existing.push({ ...log, originalIndex: index });
      grouped.set(log.correlationId, existing);
    });

    const summaries: OperationSummary[] = [];

    grouped.forEach((events, correlationId) => {
      events.sort((a, b) => {
        const timeA = new Date(a.timeGenerated).getTime();
        const timeB = new Date(b.timeGenerated).getTime();
        if (timeA !== timeB) {
          return timeA - timeB;
        }
        return a.originalIndex - b.originalIndex;
      });

      const firstEvent = events[0];
      const lastEvent = events[events.length - 1];

      const errorCount = events.filter(e => e.level === 'Error').length;
      const warningCount = events.filter(e => e.level === 'Warning').length;

      // Determinar status baseado no último evento (operation)
      const lastOperation = lastEvent.operation?.toUpperCase() || '';
      let status: 'success' | 'warning' | 'error' | 'processing' = 'success';

      if (lastOperation === 'GRAPH_CALL_SUCCESS') {
        status = 'success';
      } else if (lastOperation === 'GRAPH_CALL_FAILED' || lastOperation === 'SENT_TO_DLQ' || lastOperation === 'UNHANDLED_EXCEPTION') {
        status = 'error';
      } else if (lastOperation === 'RETRY_SCHEDULED') {
        status = 'processing'; // Retry
      } else if (lastOperation === 'GRAPH_CALL_SKIPPED') {
        status = 'warning'; // Ignorado/Skipped
      } else if (errorCount > 0) {
        status = 'error';
      } else if (warningCount > 0) {
        status = 'warning';
      }

      const getTimestamp = (event: LogEntry) => new Date(event.operationTimestamp || event.timeGenerated);
      const startTime = getTimestamp(firstEvent);
      const endTime = getTimestamp(lastEvent);
      const durationMs = endTime.getTime() - startTime.getTime();

      let duration: string;
      if (durationMs < 1000) {
        duration = `${durationMs}ms`;
      } else if (durationMs < 60000) {
        duration = `${(durationMs / 1000).toFixed(2)}s`;
      } else {
        duration = `${(durationMs / 60000).toFixed(2)}min`;
      }

      // Usar helpers de prioridade para determinar messageType e operation corretos
      const messageType = determineGroupMessageType(events);
      const operation = determineGroupOperation(events);

      const eventWithName = events.find(e => e.displayName && e.displayName.trim() !== '');
      const eventWithEmail = events.find(e => e.userPrincipalName && e.userPrincipalName.trim() !== '');

      const maxRetryCount = Math.max(...events.map(e => e.retryCount || 0));
      const hasRetries = maxRetryCount > 0;
      const hasDlq = events.some(e => e.operation?.toUpperCase() === 'SENT_TO_DLQ');

      // Extrair sectionId para operacoes de Roster/Enrollment
      const sectionId = operationInvolvesClass(messageType)
        ? extractSectionIdFromEvents(events)
        : undefined;

      summaries.push({
        correlationId,
        operation,
        messageType,
        startTime,
        endTime,
        duration,
        status,
        totalEvents: events.length, // Conta TODOS os eventos (nao filtra)
        errorCount,
        warningCount,
        externalId: firstEvent.externalId,
        displayName: eventWithName?.displayName || firstEvent.displayName,
        userPrincipalName: eventWithEmail?.userPrincipalName || firstEvent.userPrincipalName,
        hasDlqMessage: false,
        maxRetryCount,
        hasRetries,
        hasDlq,
        events: events,
        sectionId,
      });
    });

    return summaries;
  };

  /**
   * Aplica filtros de operação/userType/status às operações
   */
  const applyFilters = (ops: OperationSummary[], params: TrackingSearchParams): OperationSummary[] => {
    let filtered = ops;

    // Filtro por Tipo de Usuário (userType)
    if (params.userType) {
      const allowedMessageTypes = messageTypeMapping[params.userType];
      if (allowedMessageTypes) {
        filtered = filtered.filter(op => allowedMessageTypes.includes(op.messageType));
      }
    }

    // Filtro por Operação específica
    if (params.operation) {
      const normalizedOperation = normalizeOperation(params.operation);
      filtered = filtered.filter(op => op.messageType === normalizedOperation);
    }

    // Filtro por Status
    // O backend retorna:
    // - success: GRAPH_CALL_SUCCESS
    // - error: GRAPH_CALL_FAILED, SENT_TO_DLQ, UNHANDLED_EXCEPTION
    // - processing: RETRY_SCHEDULED, em processamento
    // - warning: GRAPH_CALL_SKIPPED
    if (params.status) {
      filtered = filtered.filter(op => {
        switch (params.status) {
          case 'success':
            return op.status === 'success';
          case 'error':
            return op.status === 'error';
          case 'retry':
            return op.status === 'processing'; // retry = processing
          case 'skipped':
            return op.status === 'warning'; // skipped = warning
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  /**
   * Handler para busca inicial
   */
  const handleSearch = async (params: TrackingSearchParams) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setOperations([]);
    setNextCursor(null);
    setHasMore(false);
    lastSearchParams.current = params;
    setStatusFilter(params.status);

    try {
      // Se tem correlationId, busca por ele especificamente (sem paginação)
      if (params.correlationId) {
        const logs = await logsService.getByCorrelationId(params.correlationId);
        const grouped = groupLogsByCorrelation(logs);
        const filtered = applyFilters(grouped, params);
        setOperations(filtered);
        setHasMore(false);
        setNextCursor(null);
      } else {
        // Busca com paginação por cursor (50 operações por página)
        // searchTerm = busca por nome (parcial)
        // externalId = busca por ID externo (exata)
        // messageType = tipo de usuário (Student, Teacher, etc)
        // operation = operação específica (NewStudent, etc)
        // status = status da operação (success, error, warning, processing)

        // Status é enviado direto para o backend (currentStatus no Azure Search)
        // Valores: processing, success, error, retry, skipped, dlq
        const result = await logsService.getGroupedOperations(
          params.dateFrom,
          params.dateTo,
          undefined, // cursor inicial
          params.searchTerm || undefined,
          50,
          params.externalId || undefined,
          params.userType || undefined,      // messageType no backend
          params.operation || undefined,     // operação específica
          params.status || undefined         // status (direto, sem conversão)
        );

        // Converter para OperationSummary
        const converted = result.operations.map(convertToOperationSummary);
        // Não precisa mais filtrar localmente pois o backend já filtrou
        setOperations(converted);
        setNextCursor(result.nextCursor);
        setHasMore(result.hasMore);
      }
    } catch (err) {
      console.error('Erro ao buscar operações:', err);
      setError('Erro ao buscar operações. Verifique se o Cosmos DB está configurado corretamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handler para carregar mais operações
   * Funciona tanto para carga inicial (últimas 48h) quanto para buscas personalizadas
   */
  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;

    setLoadingMore(true);
    setError(null);

    try {
      let result;

      // Se não há parâmetros de busca, usa endpoint de operações recentes (48h)
      if (!lastSearchParams.current) {
        result = await logsService.getRecentOperations(50, nextCursor);
        const converted = result.operations.map(convertToOperationSummary);
        setOperations(prev => [...prev, ...converted]);
      } else {
        // Busca personalizada com parâmetros
        const params = lastSearchParams.current;

        // Status é enviado direto para o backend (currentStatus no Azure Search)
        result = await logsService.getGroupedOperations(
          params.dateFrom,
          params.dateTo,
          nextCursor,
          params.searchTerm || undefined,
          50,
          params.externalId || undefined,
          params.userType || undefined,
          params.operation || undefined,
          params.status || undefined
        );

        // Converter novas operações (já filtradas pelo backend)
        const converted = result.operations.map(convertToOperationSummary);
        setOperations(prev => [...prev, ...converted]);
      }

      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Erro ao carregar mais operações:', err);
      setError('Erro ao carregar mais operações.');
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * Handler para carregar eventos de uma operação específica
   */
  const handleLoadEvents = async (correlationId: string): Promise<LogEntry[]> => {
    try {
      const logs = await logsService.getByCorrelationId(correlationId);

      setOperations(prev =>
        prev.map(op => {
          if (op.correlationId === correlationId) {
            return {
              ...op,
              events: logs,
            };
          }
          return op;
        })
      );

      return logs;
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      throw error;
    }
  };

  /**
   * Handler para export CSV
   * Com filtros ativos: export server-side (busca TODAS as operações que correspondem)
   * Sem filtros: export client-side (apenas operações carregadas na tela)
   */
  const handleExportCsv = () => {
    if (hasActiveFilters(lastSearchParams.current)) {
      startExport(lastSearchParams.current);
    } else {
      exportLocalCsv(operations);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title1 className={styles.title}>Rastreamento de Operações Educacionais</Title1>
        <Text className={styles.subtitle}>
          Acompanhe e diagnostique o ciclo de vida de operações de usuários no Microsoft 365
        </Text>
      </div>

      {/* Filtros */}
      <TrackingFilters
        onSearch={handleSearch}
        onClear={() => {
          // Limpar filtro = igual à carga inicial (TOP 50 sem filtro de data)
          // Isso ativa a estratégia de duas buscas no backend
          lastSearchParams.current = null;
          setStatusFilter(undefined);
          handleSearch({});
        }}
        onExport={handleExportCsv}
        loading={loading}
        exporting={exportState.isExporting}
      />

      {/* Error State */}
      {error && (
        <MessageBar intent="error" style={{ marginTop: '16px' }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Loading State (busca inicial) */}
      {loading && (
        <div className={styles.loadingContainer}>
          <Spinner label="Buscando operações..." size="large" />
        </div>
      )}

      {/* Results */}
      {!loading && hasSearched && (
        <>
          {operations.length > 0 ? (
            <>
              <div className={styles.resultsHeader}>
                <Text weight="semibold" size={400}>
                  {hasMore ? `${operations.length}+` : operations.length} {operations.length === 1 ? 'operação encontrada' : 'operações encontradas'}
                  {isInitialLoad ? '' : lastSearchParams.current ? ' - Busca personalizada' : ' - Última hora'}
                </Text>
              </div>
              <OperationsTable
                operations={operations}
                onLoadEvents={handleLoadEvents}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                loadingMore={loadingMore}
                statusFilter={statusFilter}
              />
            </>
          ) : (
            <div className={styles.emptyState}>
              <SearchInfo24Regular className={styles.emptyStateIcon} />
              <Text className={styles.emptyStateText}>Nenhuma operação encontrada</Text>
            </div>
          )}
        </>
      )}

      {/* Initial State - mostra apenas se não está carregando e não fez busca (não usado mais pois carrega automaticamente) */}
      {!loading && !hasSearched && (
        <div className={styles.emptyState}>
          <Spinner label="Carregando operações recentes..." size="large" />
        </div>
      )}
    </div>
  );
};
